import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ClusterService from "../../services/api/cluster";
import SATooltip from "../../components/common/SATooltip";
import SAPagination from "../../components/common/SAPagination";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import {
  API_RESPONSE,
  default_size,
  websocketAction,
  websocketModuleType,
} from "../../settings/shared/constants";
import { convertTextToTitleCase, provisionerTypeLogo } from "../../settings/shared/commonMethods";

const WORKLOAD_TYPES = [
  { id: "pods", label: "Workloads", icon: "fa-solid fa-cube" },
  { id: "deployments", label: "Deployments", icon: "fa-solid fa-cubes" },
  { id: "jobs", label: "Jobs", icon: "fa-solid fa-clock-rotate-left" },
];

const STATUS_STYLE = {
  STOPPING: { bg: "#fef3c7", color: "#92400e", label: "Stopping" },
  RUNNING: { bg: "#eaf3de", color: "#3b6d11", label: "Running" },
  READY: { bg: "#eaf3de", color: "#3b6d11", label: "Ready" },
  PENDING: { bg: "#fef3c7", color: "#92400e", label: "Pending" },
  FAILED: { bg: "#fee2e2", color: "#b91c1c", label: "Failed" },
  STOPPED: { bg: "#e2e8f0", color: "#475569", label: "Stopped" },
  UNKNOWN: { bg: "#f1f5f9", color: "#64748b", label: "Unknown" },
};

const LIVE_POLL_MS = 5000;
const WORKLOAD_PAGE_SIZE = 20;

function getLivePodId(pod) {
  if (!pod) return "";
  const candidate = String(pod.sourceId || pod.id || "").trim();
  if (!candidate || candidate.includes("/")) return String(pod.sourceId || "").trim();
  return candidate;
}

function extractLiveRaw(res) {
  if (!res || typeof res !== "object") return null;
  const fromList = extractPodsList(res);
  if (fromList.length) return fromList[0];
  const candidates = [res.pod, res.dto, res.k8sPodDTO, res.podDTO, res.data];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) return candidate;
  }
  if (res.podName || res.podStatus || res.namespace || res.sourceId) return res;
  return null;
}

function getClusterStatusStyle(status) {
  const s = String(status || "").toUpperCase();
  if (s.includes("RUN") || s.includes("READY") || s.includes("ACTIVE")) return STATUS_STYLE.RUNNING;
  if (s.includes("PEND") || s.includes("PROVISION") || s.includes("CREAT")) return STATUS_STYLE.PENDING;
  if (s.includes("FAIL") || s.includes("ERROR") || s.includes("STOP")) return STATUS_STYLE.FAILED;
  return STATUS_STYLE.UNKNOWN;
}

function getPodStatusStyle(status) {
  const s = String(status || "")
    .toUpperCase()
    .replace(/[\s_-]/g, "");

  // Stopped / deleted
  if (s.includes("DELET") || s.includes("TERMINAT") || s === "STOPPED") {
    return STATUS_STYLE.STOPPED;
  }

  // Stopping
  if (s.includes("STOPPING")) {
    return STATUS_STYLE.STOPPING;
  }

  // Failed / error states
  if (
    s.includes("FAIL") ||
    s.includes("ERROR") ||
    s.includes("CRASH") ||
    s.includes("BACKOFF") ||
    s.includes("EVICT")
  ) {
    return STATUS_STYLE.FAILED;
  }

  // Unknown
  if (s.includes("UNKNOWN")) {
    return STATUS_STYLE.UNKNOWN;
  }

  // Running / ready / successful states
  if (
    s.includes("RUN") ||
    s.includes("READY") ||
    s.includes("SUCCEED") ||
    s.includes("COMPLETED")
  ) {
    return STATUS_STYLE.RUNNING;
  }

  // Pending / initialization states
  if (
    s.includes("PEND") ||
    s.includes("PROCESS") ||
    s.includes("CONTAINERCREATING") ||
    s.includes("INIT") ||
    s.includes("WAIT") ||
    s.includes("SCHEDUL") ||
    s.includes("CREAT")
  ) {
    return STATUS_STYLE.PENDING;
  }

  return STATUS_STYLE.UNKNOWN;
}

function podStatusLabel(status) {
  const raw = String(status || "").toUpperCase();
  if (raw.includes("DELET")) return "Stopped";
  const st = getPodStatusStyle(status);
  if (st === STATUS_STYLE.STOPPED) return "Stopped";
  if (st === STATUS_STYLE.PENDING) return "Processing";
  if (st === STATUS_STYLE.STOPPING) return "Stopping";
  return convertTextToTitleCase(status) || st.label;
}

function getPodFilterKey(status) {
  const st = getPodStatusStyle(status);
  if (st === STATUS_STYLE.STOPPED) return "stopped";
  if (st === STATUS_STYLE.RUNNING || st === STATUS_STYLE.READY) return "running";
  if (st === STATUS_STYLE.PENDING) return "processing";
  if (st === STATUS_STYLE.FAILED) return "failed";
  return "other";
}

function matchesStatusFilter(status, filter) {
  if (!filter || filter === "all") return true;
  const key = getPodFilterKey(status);
  if (filter === "enabled") return key !== "stopped";
  return key === filter;
}

const POD_STATUS_FILTERS = [
  { id: "all", label: "All statuses" },
  { id: "running", label: "Running" },
  { id: "stopped", label: "Stopped" },
  { id: "failed", label: "Failed" },
];

function formatAge(value) {
  if (value == null || value === "") return "—";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return String(value);
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

function formatReady(raw, statusObj) {
  if (raw?.runningCount != null && raw?.desiredCount != null) {
    return `${raw.runningCount}/${raw.desiredCount}`;
  }
  if (raw?.ready != null && raw.ready !== "") return String(raw.ready);
  if (raw?.readyStatus) return String(raw.readyStatus);
  if (raw?.readyContainers != null && raw?.totalContainers != null) {
    return `${raw.readyContainers}/${raw.totalContainers}`;
  }
  const containers = statusObj?.containerStatuses;
  if (Array.isArray(containers) && containers.length) {
    return `${containers.filter((c) => c.ready).length}/${containers.length}`;
  }
  return "—";
}

function textOrEmpty(value) {
  if (value == null || value === "") return "";
  return String(value);
}

function normalizePod(raw, clusterId) {
  if (!raw || typeof raw !== "object") return null;
  const meta = raw.metadata && typeof raw.metadata === "object" ? raw.metadata : {};
  const spec = raw.spec && typeof raw.spec === "object" ? raw.spec : {};
  const statusObj = raw.status && typeof raw.status === "object" ? raw.status : null;
  const projectName = textOrEmpty(raw.projectName);
  const sourceId = textOrEmpty(raw.sourceId || raw.id);
  const name = textOrEmpty(raw.podName || raw.name || raw.pod || meta.name || projectName || sourceId);
  if (!name && !sourceId) return null;
  const namespace = textOrEmpty(raw.namespace || raw.ns || meta.namespace);
  const phase =
    raw.podStatus
    || raw.phase
    || raw.podPhase
    || (typeof raw.status === "string" ? raw.status : null)
    || statusObj?.phase
    || "Unknown";
  const created =
    raw.startTime
    || raw.createdDate
    || raw.creationTimestamp
    || meta.creationTimestamp
    || statusObj?.startTime
    || "";
  const restarts =
    raw.restarts
    ?? raw.restartCount
    ?? (Array.isArray(statusObj?.containerStatuses)
      ? statusObj.containerStatuses.reduce((sum, c) => sum + (c.restartCount || 0), 0)
      : 0);
  return {
    id: String(raw.id || sourceId || `${namespace}/${name}`),
    sourceId,
    name,
    projectName,
    namespace,
    ready: formatReady(raw, statusObj),
    status: String(phase),
    age: formatAge(created),
    startTime: created,
    restarts,
    node: raw.nodeName || raw.node || spec.nodeName || "—",
    zone: raw.zone || "",
    clusterName: raw.clusterName || "",
    cloudType: raw.cloudType || "",
    clusterId: String(raw.clusterId || clusterId || raw.clusterName || ""),
    message: raw.message || raw.reason || statusObj?.message || statusObj?.reason || "",
    ip: raw.podIp || raw.podIP || raw.ip || statusObj?.podIP || "",
  };
}

function extractArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.page?.content)) return value.page.content;
  return null;
}

function extractPageObject(res) {
  if (!res || typeof res !== "object") return null;
  if (res.page && typeof res.page === "object" && !Array.isArray(res.page)) return res.page;
  if (res.dto?.page && typeof res.dto.page === "object") return res.dto.page;
  return res.k8sPodDTOPage || res.podDTOPage || null;
}

function extractPodsList(res) {
  const candidates = [
    res?.page,
    res?.dto?.page,
    res?.data?.page,
    res?.pods,
    res?.k8sPodDTOPage,
    res?.podDTOPage,
    res?.k8sPodDTOS,
    res?.podDTOS,
    res?.dto?.pods,
    res?.dto?.k8sPodDTOPage,
    res?.dto?.k8sPodDTOS,
    res?.dto,
    res?.data,
    res,
  ];
  for (const candidate of candidates) {
    const list = extractArray(candidate);
    if (list) return list;
  }
  const single = res?.dto || res?.k8sPodDTO || res?.podDTO;
  if (single && typeof single === "object" && (single.name || single.podName || single.projectName || single.sourceId || single.metadata?.name)) {
    return [single];
  }
  return [];
}

function extractPodsPage(res, pageSize = WORKLOAD_PAGE_SIZE) {
  const page = extractPageObject(res);
  const content = extractPodsList(res);
  const totalElements = Number(page?.totalElements ?? res?.totalElements ?? content.length) || 0;
  const size = Number(page?.size ?? page?.pageable?.pageSize ?? res?.size ?? pageSize) || pageSize;
  let totalPages = Number(page?.totalPages ?? res?.totalPages ?? 0) || 0;
  if (!totalPages && totalElements > 0) {
    totalPages = Math.max(1, Math.ceil(totalElements / size));
  }
  const number = Number(page?.number ?? page?.pageable?.pageNumber ?? res?.number ?? 0) || 0;
  return { content, totalPages, totalElements, size, number };
}

function parseWsDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "object") return detail;
  if (typeof detail === "string") {
    try {
      return JSON.parse(detail);
    } catch {
      return null;
    }
  }
  return null;
}

function extractWsPayload(message) {
  const ninfo = message?.ninfo && typeof message.ninfo === "object" ? message.ninfo : {};
  return ninfo.dto || ninfo.msg || ninfo.data || ninfo.payload || message?.dto || message?.data || message?.pods || ninfo;
}

function extractWsPods(payload) {
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload)) return payload;
  const list =
    extractArray(payload.page)
    || extractArray(payload.k8sPodDTOPage)
    || extractArray(payload.podDTOPage)
    || extractArray(payload.k8sPodDTOS)
    || extractArray(payload.podDTOS)
    || extractArray(payload.pods)
    || extractArray(payload.dto)
    || extractArray(payload.content);
  if (list) return list;
  if (payload.name || payload.podName || payload.pod || payload.metadata?.name) return [payload];
  return [];
}

function podKey(pod) {
  if (!pod) return "";
  return pod.sourceId || pod.id || `${pod.namespace || ""}/${pod.name || pod.projectName || ""}`;
}

function mergePodList(prev, incoming, clusterId) {
  const next = [...prev];
  incoming.forEach((raw) => {
    const pod = normalizePod(raw, clusterId);
    if (!pod) return;
    const idx = next.findIndex((p) => p.id === pod.id || podKey(p) === podKey(pod));
    if (idx >= 0) {
      next[idx] = {
        ...next[idx],
        ...pod,
        projectName: pod.projectName || next[idx].projectName,
      };
    } else next.push(pod);
  });
  return next;
}

function podHealthCounts(list) {
  const source = Array.isArray(list) ? list : [];
  const running = source.filter((p) => getPodFilterKey(p.status) === "running").length;
  const processing = source.filter((p) => getPodFilterKey(p.status) === "processing").length;
  const failed = source.filter((p) => getPodFilterKey(p.status) === "failed").length;
  const stopped = source.filter((p) => getPodFilterKey(p.status) === "stopped").length;
  const enabled = source.filter((p) => getPodFilterKey(p.status) !== "stopped").length;
  return { total: source.length, enabled, running, processing, failed, stopped };
}

const PAGE_CSS = `
  .cw-page {
    font-family: 'DM Sans', system-ui, sans-serif;
    min-height: 100%;
    background: #f5f6fa;
    color: #0f172a;
  }

  .cw-hero {
    background:
      radial-gradient(ellipse 60% 80% at 0% 0%, rgba(61,145,228,0.12), transparent 55%),
      linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border-bottom: 1px solid rgba(15,23,42,0.06);
    padding: 22px 28px 18px;
  }
  .cw-hero-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }
  .cw-kicker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--activemenu, #3d91e4);
    margin-bottom: 6px;
  }
  .cw-hero h1 {
    margin: 0 0 4px;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .cw-hero p {
    margin: 0;
    font-size: 13px;
    color: #64748b;
    max-width: 480px;
    line-height: 1.45;
  }
  .cw-hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .cw-btn {
    border: 1px solid rgba(15,23,42,0.1);
    background: #fff;
    color: #334155;
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 12.5px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .cw-btn:hover {
    border-color: rgba(61,145,228,0.35);
    color: var(--active-color, #033b70);
  }
  .cw-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .cw-btn.primary {
    background: var(--activemenu, #3d91e4);
    border-color: transparent;
    color: #fff;
  }
  .cw-btn.primary:hover { filter: brightness(0.96); color: #fff; }

  .cw-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    background: #fff;
    border: 1px solid rgba(15,23,42,0.08);
    border-radius: 16px;
    padding: 12px 12px 12px 12px;
    box-shadow: 0 8px 24px rgba(15,23,42,0.03);
  }
  .cw-cluster-switch {
    position: relative;
    min-width: 240px;
    max-width: 420px;
    flex: 1;
  }
  .cw-cluster-switch-btn {
    width: 100%;
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) 16px;
    gap: 10px;
    align-items: center;
    text-align: left;
    border: 1px solid rgba(15,23,42,0.12);
    background: #f8fafc;
    border-radius: 12px;
    padding: 8px 10px;
    cursor: pointer;
  }
  .cw-cluster-switch-btn:hover,
  .cw-cluster-switch-btn.is-open {
    border-color: rgba(61,145,228,0.45);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(61,145,228,0.12);
  }
  .cw-cluster-switch-btn:disabled {
    cursor: default;
    opacity: 0.85;
    box-shadow: none;
  }
  .cw-summary-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid rgba(15,23,42,0.06);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
  }
  .cw-summary-avatar img { width: 22px; height: 22px; object-fit: contain; }
  .cw-cluster-switch-copy { min-width: 0; }
  .cw-cluster-switch-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .cw-cluster-switch-name {
    margin-top: 1px;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cw-cluster-switch-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    min-width: 0;
  }
  .cw-cluster-switch-chevron {
    color: #64748b;
    font-size: 12px;
    justify-self: end;
  }
  .cw-cluster-menu {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 6px);
    background: #fff;
    border: 1px solid rgba(15,23,42,0.1);
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(15,23,42,0.14);
    z-index: 40;
    max-height: 280px;
    overflow: auto;
    padding: 6px;
  }
  .cw-cluster-option {
    width: 100%;
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) auto;
    gap: 8px;
    align-items: center;
    text-align: left;
    border: none;
    background: transparent;
    border-radius: 9px;
    padding: 8px;
    cursor: pointer;
  }
  .cw-cluster-option:hover { background: #f8fafc; }
  .cw-cluster-option.is-active { background: rgba(61,145,228,0.08); }
  .cw-cluster-option-name {
    font-size: 13px;
    font-weight: 650;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cw-cluster-option-meta {
    font-size: 11px;
    color: #64748b;
    margin-top: 1px;
  }
  .cw-summary-metrics {
    display: flex;
    align-items: stretch;
    gap: 8px;
    flex-shrink: 0;
  }
  .cw-metric-pill {
    min-width: 76px;
    padding: 8px 12px;
    border-radius: 12px;
    background: #f8fafc;
    text-align: left;
    border: 1px solid transparent;
    cursor: pointer;
  }
  .cw-metric-pill:hover { filter: brightness(0.98); }
  .cw-metric-pill.is-active {
    box-shadow: 0 0 0 2px rgba(61,145,228,0.28);
    border-color: rgba(61,145,228,0.35);
  }
  .cw-metric-pill.is-running { background: #eaf3de; }
  .cw-metric-pill.is-processing { background: #fef3c7; }
  .cw-metric-pill.is-failed { background: #fee2e2; }
  .cw-metric-pill.is-stopped { background: #e2e8f0; }
  .cw-metric-pill.is-enabled { background: #e0f2fe; }
  .cw-metric-pill-value {
    font-size: 18px;
    font-weight: 750;
    letter-spacing: -0.03em;
    line-height: 1.1;
    color: #0f172a;
  }
  .cw-metric-pill.is-running .cw-metric-pill-value { color: #3b6d11; }
  .cw-metric-pill.is-processing .cw-metric-pill-value { color: #92400e; }
  .cw-metric-pill.is-failed .cw-metric-pill-value { color: #b91c1c; }
  .cw-metric-pill.is-stopped .cw-metric-pill-value { color: #475569; }
  .cw-metric-pill.is-enabled .cw-metric-pill-value { color: #075985; }
  .cw-metric-pill-label {
    margin-top: 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
  }
  .cw-metric-pill.is-running .cw-metric-pill-label { color: #3b6d11; }
  .cw-metric-pill.is-processing .cw-metric-pill-label { color: #92400e; }
  .cw-metric-pill.is-failed .cw-metric-pill-label { color: #b91c1c; }
  .cw-metric-pill.is-stopped .cw-metric-pill-label { color: #475569; }
  .cw-metric-pill.is-enabled .cw-metric-pill-label { color: #075985; }
  .cw-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 650;
    white-space: nowrap;
  }
  .cw-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  .cw-body {
    padding: 20px 28px 40px;
    margin: 0 auto;
  }
  .cw-body.has-live {
    max-width: 1320px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    align-items: start;
    gap: 16px;
  }

  .cw-shell {
    background: #fff;
    border: 1px solid rgba(15,23,42,0.07);
    border-radius: 16px;
    overflow: visible;
    box-shadow: 0 8px 24px rgba(15,23,42,0.03);
  }
  .cw-shell-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(15,23,42,0.06);
    background: linear-gradient(180deg, #fff, #fafbfc);
    border-radius: 16px 16px 0 0;
    position: relative;
    z-index: 30;
    overflow: visible;
  }
  .cw-tabs {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border-radius: 12px;
    background: #f1f5f9;
    overflow-x: auto;
    max-width: 100%;
  }
  .cw-tab {
    border: none;
    background: transparent;
    color: #64748b;
    border-radius: 9px;
    padding: 7px 11px;
    font-size: 12.5px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    white-space: nowrap;
  }
  .cw-tab.is-active {
    background: #fff;
    color: var(--active-color, #033b70);
    box-shadow: 0 1px 3px rgba(15,23,42,0.08);
  }
  .cw-tab i { font-size: 11px; }
  .cw-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .cw-ns {
    border: 1px solid rgba(15,23,42,0.1);
    background: #fff;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 12.5px;
    color: #334155;
    outline: none;
  }
  .cw-ns-select {
    position: relative;
    min-width: 180px;
    z-index: 1;
  }
  .cw-ns-select.is-open {
    z-index: 40;
  }
  .cw-ns-select-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid rgba(15,23,42,0.1);
    background: #fff;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 12.5px;
    color: #334155;
    cursor: pointer;
    text-align: left;
  }
  .cw-ns-select-btn:hover,
  .cw-ns-select-btn.is-open {
    border-color: rgba(61,145,228,0.45);
    box-shadow: 0 0 0 3px rgba(61,145,228,0.12);
  }
  .cw-ns-select-btn i.cw-ns-select-ico {
    color: #94a3b8;
    font-size: 11px;
    flex-shrink: 0;
  }
  .cw-ns-select-value {
    flex: 1;
    min-width: 0;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  .cw-ns-select-btn.is-all .cw-ns-select-value {
    font-family: inherit;
    font-weight: 600;
  }
  .cw-ns-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    width: min(280px, 80vw);
    background: #fff;
    border: 1px solid rgba(15,23,42,0.1);
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(15,23,42,0.14);
    z-index: 80;
    overflow: visible;
  }
  .cw-ns-search {
    position: relative;
    padding: 8px 8px 6px;
    border-bottom: 1px solid rgba(15,23,42,0.06);
  }
  .cw-ns-search i {
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 11px;
  }
  .cw-ns-search input {
    width: 100%;
    border: 1px solid rgba(15,23,42,0.1);
    background: #f8fafc;
    border-radius: 8px;
    padding: 7px 8px 7px 28px;
    font-size: 12.5px;
    outline: none;
  }
  .cw-ns-search input:focus {
    border-color: rgba(61,145,228,0.45);
    background: #fff;
  }
  .cw-ns-list {
    max-height: 220px;
    overflow: auto;
    padding: 6px;
  }
  .cw-ns-option {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border: none;
    background: transparent;
    border-radius: 8px;
    padding: 8px 10px;
    cursor: pointer;
    text-align: left;
    font-size: 12.5px;
    color: #0f172a;
  }
  .cw-ns-option:hover { background: #f8fafc; }
  .cw-ns-option.is-active { background: rgba(61,145,228,0.08); color: #1d4ed8; font-weight: 650; }
  .cw-ns-option-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  .cw-ns-option.is-all .cw-ns-option-name {
    font-family: inherit;
    font-weight: 650;
  }
  .cw-ns-empty {
    padding: 14px 10px;
    text-align: center;
    font-size: 12px;
    color: #94a3b8;
  }

  .cw-table-wrap {
    overflow-x: auto;
    border-radius: 0 0 16px 16px;
    position: relative;
    z-index: 0;
  }
  .cw-shell:has(.cw-pagination) .cw-table-wrap {
    border-radius: 0;
  }
  .cw-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
  }
  .cw-table th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(15,23,42,0.06);
    background: #fafbfc;
  }
  .cw-table td {
    padding: 14px 16px;
    border-bottom: 1px solid rgba(15,23,42,0.05);
    font-size: 13px;
    color: #334155;
    vertical-align: middle;
  }
  .cw-table tr:last-child td { border-bottom: none; }
  .cw-table tbody tr.cw-row-click {
    cursor: pointer;
    transition: background 0.12s;
  }
  .cw-table tbody tr.cw-row-click:hover td {
    background: rgba(61,145,228,0.04);
  }
  .cw-table tbody tr.cw-row-click.is-selected td {
    background: rgba(61,145,228,0.08);
  }
  .cw-row-name {
    font-weight: 650;
    color: #0f172a;
  }
  .cw-row-sub {
    font-size: 11.5px;
    color: #94a3b8;
    margin-top: 2px;
  }
  .cw-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: #475569;
  }
  .cw-copy {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    text-align: left;
    color: inherit;
  }
  .cw-copy .cw-mono {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cw-copy i {
    font-size: 11px;
    color: #94a3b8;
    flex-shrink: 0;
  }
  .cw-copy:hover i,
  .cw-copy:focus-visible i {
    color: var(--activemenu, #3d91e4);
  }
  .cw-row-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
  }
  .cw-stop-btn {
    border: 1px solid rgba(15,23,42,0.1);
    background: #fff;
    color: #334155;
    border-radius: 8px;
    padding: 5px 9px;
    font-size: 12px;
    font-weight: 650;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    white-space: nowrap;
  }
  .cw-stop-btn i { font-size: 12px; color: #b91c1c; }
  .cw-stop-btn:hover {
    border-color: rgba(185,28,28,0.28);
    background: #fef2f2;
    color: #991b1b;
  }
  .cw-stop-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: #fff;
    color: #94a3b8;
  }
  .cw-stop-btn:disabled i { color: #94a3b8; }
  .cw-table th.cw-col-actions,
  .cw-table td.cw-col-actions {
    width: 92px;
    text-align: right;
  }
  .cw-confirm-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,0.38);
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  .cw-confirm {
    width: min(420px, 100%);
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 24px 60px rgba(15,23,42,0.18);
    padding: 22px 22px 18px;
  }
  .cw-confirm-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: #fef2f2;
    color: #b91c1c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    margin-bottom: 12px;
  }
  .cw-confirm h3 {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }
  .cw-confirm p {
    margin: 0 0 14px;
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
  }
  .cw-confirm-pod {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid rgba(15,23,42,0.06);
    margin-bottom: 16px;
  }
  .cw-confirm-pod-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    word-break: break-all;
  }
  .cw-confirm-pod-ns {
    font-size: 11.5px;
    color: #64748b;
    margin-top: 2px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }
  .cw-confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .cw-confirm-actions .cw-btn.danger {
    background: #b91c1c;
    border-color: transparent;
    color: #fff;
  }
  .cw-confirm-actions .cw-btn.danger:hover { filter: brightness(0.96); color: #fff; }

  .cw-live-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    max-width: 100%;
  }
  .cw-live-btn:hover .cw-status {
    filter: brightness(0.97);
  }
  .cw-live-icon {
    width: 22px;
    height: 22px;
    border-radius: 7px;
    border: 1px solid rgba(15,23,42,0.1);
    background: #fff;
    color: var(--activemenu, #3d91e4);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    flex-shrink: 0;
  }
  .cw-live-btn.is-listening .cw-live-icon {
    border-color: rgba(61,145,228,0.35);
    background: rgba(61,145,228,0.08);
  }
  .cw-live-icon i.is-pulse,
  .cw-live-kicker i {
    animation: cwPulse 1.4s ease infinite;
  }
  @keyframes cwPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }

  .cw-split {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  }
  .cw-live-panel {
    border: 1px solid rgba(15,23,42,0.07);
    background: #fff;
    border-radius: 16px;
    min-height: 0;
    max-height: calc(100vh - 140px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(15,23,42,0.03);
    position: sticky;
    top: 16px;
  }
  .cw-live-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid rgba(15,23,42,0.06);
  }
  .cw-live-kicker {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--activemenu, #3d91e4);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }
  .cw-live-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    word-break: break-all;
  }
  .cw-live-sub {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
  }
  .cw-live-sub-extra {
    color: #64748b;
  }
  .cw-live-close {
    border: 1px solid rgba(15,23,42,0.1);
    background: #fff;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    cursor: pointer;
    color: #64748b;
    flex-shrink: 0;
  }
  .cw-live-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    padding: 12px 16px 16px;
  }
  .cw-live-meta-item {
    min-width: 0;
  }
  .cw-live-meta-label {
    font-size: 10.5px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .cw-live-meta-value {
    margin-top: 3px;
    font-size: 12.5px;
    color: #0f172a;
    word-break: break-word;
  }
  .cw-live-error {
    color: #b91c1c;
    font-size: 12.5px;
    padding: 8px 16px 0;
  }

  @media (max-width: 1100px) {
    .cw-body.has-live {
      grid-template-columns: minmax(0, 1fr) 320px;
    }
  }
  @media (max-width: 860px) {
    .cw-body.has-live {
      grid-template-columns: 1fr;
    }
    .cw-live-panel {
      position: static;
      max-height: 480px;
    }
  }

  .cw-empty {
    padding: 56px 24px;
    text-align: center;
  }
  .cw-empty-art {
    width: 72px;
    height: 72px;
    margin: 0 auto 14px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background:
      linear-gradient(145deg, rgba(61,145,228,0.14), rgba(99,102,241,0.1));
    color: var(--activemenu, #3d91e4);
    font-size: 26px;
    position: relative;
  }
  .cw-empty-art::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 26px;
    border: 1px dashed rgba(61,145,228,0.25);
  }
  .cw-empty h3 {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 700;
  }
  .cw-empty p {
    margin: 0 auto 16px;
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
    max-width: 420px;
  }
  .cw-empty-steps {
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .cw-empty-step {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid rgba(15,23,42,0.08);
    font-size: 11.5px;
    color: #475569;
    font-weight: 550;
  }

  .cw-skel {
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
    background-size: 400% 100%;
    animation: cwShimmer 1.2s ease infinite;
  }
  @keyframes cwShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: 0 0; }
  }

  @media (max-width: 900px) {
    .cw-hero, .cw-body { padding-left: 16px; padding-right: 16px; }
    .cw-summary { flex-wrap: wrap; }
    .cw-summary-metrics { width: 100%; }
    .cw-metric-pill { flex: 1; }
  }

  .cw-pagination {
    padding: 8px 14px 12px;
    border-top: 1px solid rgba(15,23,42,0.06);
    border-radius: 0 0 16px 16px;
  }
  .cw-pagination .page-footer {
    margin-top: 0 !important;
    padding: 4px 0 !important;
    border-radius: 0;
    min-height: 0;
  }
  .cw-pagination .pagination-info {
    font-size: 11px !important;
    line-height: 1.2;
  }
  .cw-pagination .go-to-page {
    margin-top: 4px !important;
  }
  .cw-pagination .go-to-page label {
    font-size: 11px;
    color: #64748b;
    white-space: nowrap;
  }
  .cw-pagination .go-to-page .form-control {
    height: 26px;
    font-size: 12px;
    padding: 2px 6px;
  }
  .cw-pagination .go-to-page .btn {
    height: 26px;
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    background: var(--activemenu, #3d91e4) !important;
    border-color: var(--activemenu, #3d91e4) !important;
    color: #fff !important;
  }
  .cw-pagination .pagination {
    gap: 2px;
  }
  .cw-pagination .pagination .page-link {
    padding: 2px 8px !important;
    margin: 0 !important;
  }
`;

function TableSkeleton() {
  return (
    <tbody>
      {[0, 1, 2, 3, 4].map((i) => (
        <tr key={i}>
          <td><div className="cw-skel" style={{ width: "55%" }} /></td>
          <td><div className="cw-skel" style={{ width: "40%" }} /></td>
          <td><div className="cw-skel" style={{ width: "35%" }} /></td>
          <td><div className="cw-skel" style={{ width: "28%" }} /></td>
          <td><div className="cw-skel" style={{ width: "18%" }} /></td>
        </tr>
      ))}
    </tbody>
  );
}

function CopyNamespace({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="cw-mono">—</span>;
  const onCopy = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button
      type="button"
      className="cw-copy"
      onClick={onCopy}
      title={copied ? "Copied" : "Copy namespace"}
      aria-label={copied ? "Namespace copied" : "Copy namespace"}
    >
      <span className="cw-mono">{value}</span>
      <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`} aria-hidden="true" />
    </button>
  );
}

const ClusterWorkloads = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedClusterId, setSelectedClusterId] = useState("");
  const [workloadType, setWorkloadType] = useState("pods");
  const [namespace, setNamespace] = useState("All namespaces");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pods, setPods] = useState([]);
  const [loadingPods, setLoadingPods] = useState(false);
  const [refreshingPods, setRefreshingPods] = useState(false);
  const [selectedPod, setSelectedPod] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState("");
  const [podToDelete, setPodToDelete] = useState(null);
  const [deletingPodId, setDeletingPodId] = useState("");
  const [clusterMenuOpen, setClusterMenuOpen] = useState(false);
  const [nsMenuOpen, setNsMenuOpen] = useState(false);
  const [nsQuery, setNsQuery] = useState("");
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusQuery, setStatusQuery] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(WORKLOAD_PAGE_SIZE);

  const selectedPodRef = useRef(null);
  const clusterIdRef = useRef("");
  const pageRef = useRef(0);
  const liveReqRef = useRef(0);
  const clusterSwitchRef = useRef(null);
  const nsSelectRef = useRef(null);
  const statusSelectRef = useRef(null);

  useEffect(() => {
    selectedPodRef.current = selectedPod;
  }, [selectedPod]);

  useEffect(() => {
    clusterIdRef.current = selectedClusterId;
    setClusterMenuOpen(false);
    setNsMenuOpen(false);
    setNsQuery("");
    setStatusMenuOpen(false);
    setStatusQuery("");
    setNamespace("All namespaces");
    setStatusFilter("all");
    setPage(0);
  }, [selectedClusterId]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    if (!clusterMenuOpen && !nsMenuOpen && !statusMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (clusterMenuOpen && clusterSwitchRef.current && !clusterSwitchRef.current.contains(event.target)) {
        setClusterMenuOpen(false);
      }
      if (nsMenuOpen && nsSelectRef.current && !nsSelectRef.current.contains(event.target)) {
        setNsMenuOpen(false);
        setNsQuery("");
      }
      if (statusMenuOpen && statusSelectRef.current && !statusSelectRef.current.contains(event.target)) {
        setStatusMenuOpen(false);
        setStatusQuery("");
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setClusterMenuOpen(false);
        setNsMenuOpen(false);
        setNsQuery("");
        setStatusMenuOpen(false);
        setStatusQuery("");
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [clusterMenuOpen, nsMenuOpen, statusMenuOpen]);

  useEffect(() => {
    const id = "sa-cluster-workloads-styles";
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement("style");
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = PAGE_CSS;
  }, []);

  const loadClusters = (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    return ClusterService.getClusterList(0, default_size)
      .then((res) => {
        if (res?.status === API_RESPONSE.SUCCESS) {
          const list = res?.k8sClusterDTOPage?.content || [];
          setClusters(list);
          setSelectedClusterId((prev) => {
            if (prev && list.some((c) => String(c.id || c.clusterId) === String(prev))) return prev;
            return list[0]?.id || list[0]?.clusterId || "";
          });
        } else {
          setClusters([]);
        }
      })
      .catch(() => setClusters([]))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  const loadPods = useCallback((clusterId, isRefresh = false, pageNumber) => {
    if (!clusterId) {
      setPods([]);
      setTotalPages(0);
      setTotalElements(0);
      setLoadingPods(false);
      setRefreshingPods(false);
      return Promise.resolve();
    }
    const pageToLoad = pageNumber == null ? pageRef.current : pageNumber;
    if (isRefresh) setRefreshingPods(true);
    else setLoadingPods(true);
    return ClusterService.listPods(clusterId, pageToLoad, WORKLOAD_PAGE_SIZE)
      .then((res) => {
        const failed =
          res?.status === API_RESPONSE.FAILED
          || res?.status === API_RESPONSE.FAILURE
          || Boolean(res?.errorCode || res?.errorMessage);
        if (failed) {
          if (!isRefresh) {
            setPods([]);
            setTotalPages(0);
            setTotalElements(0);
          }
          return;
        }
        const paged = extractPodsPage(res, WORKLOAD_PAGE_SIZE);
        const list = paged.content
          .map((item) => normalizePod(item, clusterId))
          .filter(Boolean);
        setPods(list);
        setTotalPages(paged.totalPages);
        setTotalElements(paged.totalElements);
        setPageSize(paged.size || WORKLOAD_PAGE_SIZE);
        setSelectedPod((prev) => {
          if (!prev) return prev;
          const updated = list.find((p) => p.id === prev.id || podKey(p) === podKey(prev));
          return updated ? { ...prev, ...updated } : prev;
        });
      })
      .catch(() => {
        if (!isRefresh) {
          setPods([]);
          setTotalPages(0);
          setTotalElements(0);
        }
      })
      .finally(() => {
        setLoadingPods(false);
        setRefreshingPods(false);
      });
  }, []);

  const fetchLiveStatus = useCallback((pod, { silent = false } = {}) => {
    const podId = getLivePodId(pod);
    if (!podId) {
      if (!silent) setLiveError("Missing pod id for live status");
      return Promise.resolve();
    }
    const req = ++liveReqRef.current;
    if (!silent) setLiveLoading(true);
    setLiveError("");
    return ClusterService.getPodLiveStatus(podId)
      .then((res) => {
        if (req !== liveReqRef.current) return;
        const failed =
          res?.status === API_RESPONSE.FAILED
          || res?.status === API_RESPONSE.FAILURE
          || Boolean(res?.errorCode);
        const raw = extractLiveRaw(res);
        if (failed && !raw) {
          setLiveError(res?.errorMessage || "Failed to load live status");
          return;
        }
        if (raw) {
          const clusterId = clusterIdRef.current;
          setPods((prev) => mergePodList(prev, [raw], clusterId));
          setSelectedPod((prev) => {
            const mergedRaw = {
              ...(prev || {}),
              ...raw,
              podName: raw.podName || raw.name || prev?.name,
              namespace: raw.namespace || prev?.namespace,
              sourceId: raw.sourceId || prev?.sourceId,
              id: raw.id || prev?.id,
              projectName: raw.projectName || prev?.projectName,
            };
            return normalizePod(mergedRaw, clusterId) || prev;
          });
        }
      })
      .catch(() => {
        if (req !== liveReqRef.current) return;
        setLiveError("Failed to load live status");
      })
      .finally(() => {
        if (req === liveReqRef.current) setLiveLoading(false);
      });
  }, []);

  useEffect(() => {
    loadClusters();
  }, []);

  useEffect(() => {
    liveReqRef.current += 1;
    setSelectedPod(null);
    setLiveError("");
    setLiveLoading(false);
    loadPods(selectedClusterId, false, page);
  }, [selectedClusterId, page, loadPods]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  useEffect(() => {
    const handlePodMessage = (event) => {
      const message = parseWsDetail(event?.detail);
      if (!message) return;
      const moduleType = message.ninfo?.moduleType || message.moduleType;
      if (moduleType && moduleType !== websocketModuleType.POD && moduleType !== "POD") return;

      const action = message.ninfo?.action || message.action;
      const clusterId = clusterIdRef.current;
      const payload = extractWsPayload(message);
      const incoming = extractWsPods(payload);

      if (action === websocketAction.REFRESH && clusterId) {
        loadPods(clusterId, true, pageRef.current);
      }

      if (incoming.length) {
        setPods((prev) => {
          const next = [...prev];
          incoming.forEach((raw) => {
            const pod = normalizePod(raw, clusterId);
            if (!pod) return;
            const idx = next.findIndex((p) => p.id === pod.id || podKey(p) === podKey(pod));
            if (idx >= 0) {
              next[idx] = {
                ...next[idx],
                ...pod,
                projectName: pod.projectName || next[idx].projectName,
              };
            }
          });
          return next;
        });
        setSelectedPod((prev) => {
          if (!prev) return prev;
          const match = incoming
            .map((item) => normalizePod(item, clusterId))
            .find((p) => p && (p.id === prev.id || podKey(p) === podKey(prev)));
          return match ? { ...prev, ...match } : prev;
        });
      }
    };

    window.addEventListener("podMessageReceived", handlePodMessage);
    return () => window.removeEventListener("podMessageReceived", handlePodMessage);
  }, [loadPods]);

  const selectedCluster = useMemo(
    () => clusters.find((c) => String(c.id || c.clusterId) === String(selectedClusterId)),
    [clusters, selectedClusterId]
  );

  const activeType = WORKLOAD_TYPES.find((t) => t.id === workloadType) || WORKLOAD_TYPES[0];
  const clusterStatus = getClusterStatusStyle(selectedCluster?.clusterStatus);
  const isPodsTab = workloadType === "pods";
  const showLive = isPodsTab && !!selectedPod;

  const namespaces = useMemo(() => {
    const fromPods = [...new Set(pods.map((p) => p.namespace).filter(Boolean))].sort();
    const list = ["All namespaces", ...fromPods];
    return list.includes(namespace) ? list : [namespace, ...list];
  }, [pods, namespace]);

  const searchableNamespaces = useMemo(() => {
    const q = nsQuery.trim().toLowerCase();
    if (!q) return namespaces;
    return namespaces.filter((ns) => ns.toLowerCase().includes(q));
  }, [namespaces, nsQuery]);

  const searchableStatuses = useMemo(() => {
    const q = statusQuery.trim().toLowerCase();
    if (!q) return POD_STATUS_FILTERS;
    return POD_STATUS_FILTERS.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [statusQuery]);

  const statusFilterLabel =
    POD_STATUS_FILTERS.find((opt) => opt.id === statusFilter)?.label || "All statuses";

  const filteredPods = useMemo(() => {
    return pods.filter((p) => {
      if (namespace !== "All namespaces" && p.namespace !== namespace) return false;
      return matchesStatusFilter(p.status, statusFilter);
    });
  }, [pods, namespace, statusFilter]);

  const health = useMemo(() => podHealthCounts(pods), [pods]);
  const totalCount = totalElements || health.total;

  const applyStatusFilter = (next) => {
    setStatusFilter(next);
    setPage(0);
  };

  const applyNamespace = (next) => {
    setNamespace(next);
    setPage(0);
  };

  const goToClusters = () => {
    const next = new URLSearchParams(searchParams);
    next.set("view", "cluster");
    setSearchParams(next, { replace: false });
  };

  const livePodId = getLivePodId(selectedPod);

  useEffect(() => {
    if (!livePodId || !selectedPod) return undefined;
    fetchLiveStatus(selectedPod);
    const timer = setInterval(() => {
      const current = selectedPodRef.current;
      if (current) fetchLiveStatus(current, { silent: true });
    }, LIVE_POLL_MS);
    return () => {
      clearInterval(timer);
      liveReqRef.current += 1;
    };
  }, [livePodId, fetchLiveStatus]);

  const openLiveStatus = (pod) => {
    setLiveError("");
    setSelectedPod(pod);
  };

  const closeLiveStatus = () => {
    liveReqRef.current += 1;
    setSelectedPod(null);
    setLiveError("");
    setLiveLoading(false);
  };

  const askDeletePod = (pod) => {
    const podId = getLivePodId(pod);
    const status = getPodStatusStyle(pod.status);
    if (
      !podId ||
      status === STATUS_STYLE.STOPPED ||
      status === STATUS_STYLE.PROCESSING
    ) {
      return;
    }
    setPodToDelete(pod);
  };

  const cancelDeletePod = () => {
    if (deletingPodId) return;
    setPodToDelete(null);
  };

  const confirmDeletePod = () => {
    const pod = podToDelete;
    const podId = getLivePodId(pod);
    if (!pod || !podId) {
      setPodToDelete(null);
      return;
    }
    setDeletingPodId(podId);
    ClusterService.deletePod(podId)
      .then((res) => {
        const failed =
          res?.status === API_RESPONSE.FAILED
          || res?.status === API_RESPONSE.FAILURE
          || Boolean(res?.errorCode || res?.errorMessage);
        if (failed) {
          showNotificationGlobal(res?.errorMessage || "Failed to stop workload", "error", "bottomRight");
          return;
        }
        showNotificationGlobal("Workload stop started", "success", "bottomRight");
        setSelectedPod((prev) => (prev && getLivePodId(prev) === podId ? null : prev));
        if (selectedClusterId) loadPods(selectedClusterId, true, pageRef.current);
      })
      .catch(() => {
        showNotificationGlobal("Failed to stop workload", "error", "bottomRight");
      })
      .finally(() => {
        setDeletingPodId("");
        setPodToDelete(null);
      });
  };

  const refreshAll = () => {
    loadClusters(true);
    if (selectedClusterId) loadPods(selectedClusterId, true, pageRef.current);
  };

  const tableBusy = loading || (isPodsTab && loadingPods && pods.length === 0);
  const selectedStatus = getPodStatusStyle(selectedPod?.status);

  return (
    <div className="cw-page">
      <div className="cw-hero">
        <div className="cw-hero-top">
          <div>
            <div className="cw-kicker">
              <i className="fa-solid fa-cubes" aria-hidden="true" />
              Cluster · Workloads
            </div>
            <h1>Resources</h1>
            <p>Browse workloads, deployments, and other resources across your Kubernetes clusters.</p>
          </div>
          <div className="cw-hero-actions">
            <button
              type="button"
              className="cw-btn"
              onClick={refreshAll}
              disabled={loading || refreshing || refreshingPods}
            >
              <i className={`fa-solid fa-rotate-right${refreshing || refreshingPods ? " fa-spin" : ""}`} />
              Refresh
            </button>
            <button type="button" className="cw-btn primary" onClick={goToClusters}>
              <i className="fa-solid fa-plus" />
              Manage clusters
            </button>
          </div>
        </div>

        <div className="cw-summary">
          <div className="cw-cluster-switch" ref={clusterSwitchRef}>
            {loading && clusters.length === 0 ? (
              <button type="button" className="cw-cluster-switch-btn" disabled>
                <span className="cw-summary-avatar">
                  <div className="cw-skel" style={{ width: 22, height: 22, borderRadius: 6 }} />
                </span>
                <span className="cw-cluster-switch-copy">
                  <div className="cw-skel" style={{ width: 120, height: 10, marginBottom: 8 }} />
                  <div className="cw-skel" style={{ width: 180, height: 14 }} />
                </span>
              </button>
            ) : (
              <button
                type="button"
                className={`cw-cluster-switch-btn${clusterMenuOpen ? " is-open" : ""}`}
                disabled={clusters.length === 0}
                aria-haspopup="listbox"
                aria-expanded={clusterMenuOpen}
                onClick={() => {
                  if (clusters.length === 0) return;
                  setNsMenuOpen(false);
                  setNsQuery("");
                  setStatusMenuOpen(false);
                  setStatusQuery("");
                  setClusterMenuOpen((open) => !open);
                }}
              >
                <span className="cw-summary-avatar">
                  {selectedCluster && provisionerTypeLogo(selectedCluster.provisionerType || selectedCluster.cloudType) ? (
                    <img src={provisionerTypeLogo(selectedCluster.provisionerType || selectedCluster.cloudType)} alt="" />
                  ) : (
                    <i className="fa-solid fa-dharmachakra" style={{ color: "#64748b" }} />
                  )}
                </span>
                <span className="cw-cluster-switch-copy">
                  <div className="cw-cluster-switch-label">
                    {clusters.length === 0 ? "Cluster" : `${clusters.length} cluster${clusters.length === 1 ? "" : "s"}`}
                  </div>
                  <div className="cw-cluster-switch-name">
                    {clusters.length === 0
                      ? "No clusters yet"
                      : (selectedCluster?.name || selectedCluster?.clusterName || selectedClusterId)}
                  </div>
                  <div className="cw-cluster-switch-meta">
                    {clusters.length === 0 ? (
                      <span className="cw-cluster-option-meta">Create one to inspect workloads</span>
                    ) : (
                      <>
                        <span className="cw-status" style={{ background: clusterStatus.bg, color: clusterStatus.color }}>
                          <span className="cw-status-dot" />
                          {clusterStatus.label}
                        </span>
                        <span className="cw-cluster-option-meta">
                          {convertTextToTitleCase(selectedCluster?.provisionerType || selectedCluster?.cloudType || "Kubernetes")}
                        </span>
                      </>
                    )}
                  </div>
                </span>
                {clusters.length > 0 ? (
                  <i className={`fa-solid fa-chevron-${clusterMenuOpen ? "up" : "down"} cw-cluster-switch-chevron`} />
                ) : null}
              </button>
            )}
            {clusterMenuOpen && clusters.length > 0 ? (
              <div className="cw-cluster-menu" role="listbox" aria-label="Clusters">
                {clusters.map((c) => {
                  const id = c.id || c.clusterId;
                  const active = String(id) === String(selectedClusterId);
                  const st = getClusterStatusStyle(c.clusterStatus);
                  const logo = provisionerTypeLogo(c.provisionerType || c.cloudType);
                  return (
                    <button
                      key={id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`cw-cluster-option${active ? " is-active" : ""}`}
                      onClick={() => {
                        setSelectedClusterId(id);
                        setPage(0);
                        setNamespace("All namespaces");
                        setStatusFilter("all");
                        setClusterMenuOpen(false);
                      }}
                    >
                      <span className="cw-summary-avatar" style={{ width: 32, height: 32, borderRadius: 8 }}>
                        {logo ? <img src={logo} alt="" /> : <i className="fa-solid fa-dharmachakra" style={{ color: "#64748b", fontSize: 13 }} />}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <div className="cw-cluster-option-name">{c.name || c.clusterName || id}</div>
                        <div className="cw-cluster-option-meta">
                          {convertTextToTitleCase(c.provisionerType || c.cloudType || "Kubernetes")}
                        </div>
                      </span>
                      <span className="cw-status" style={{ background: st.bg, color: st.color }}>
                        <span className="cw-status-dot" />
                        {st.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div className="cw-summary-metrics" aria-label="Pod counts">
            {[
              { key: "all", label: "Total", value: totalCount },
              { key: "running", label: "Running", value: health.running },
              { key: "stopped", label: "Stopped", value: health.stopped },
              { key: "failed", label: "Failed", value: health.failed },
            ].map((stat) => (
              <button
                key={stat.key}
                type="button"
                className={`cw-metric-pill is-${stat.key}${statusFilter === stat.key ? " is-active" : ""}`}
                onClick={() => applyStatusFilter(stat.key !== "all" && statusFilter === stat.key ? "all" : stat.key)}
                aria-pressed={statusFilter === stat.key}
              >
                <div className="cw-metric-pill-value">
                  {loadingPods && pods.length === 0 ? "—" : selectedCluster ? stat.value : "—"}
                </div>
                <div className="cw-metric-pill-label">{stat.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`cw-body${showLive ? " has-live" : ""}`}>
        <div className="cw-shell">
          <div className="cw-shell-toolbar">
            <div className="cw-tabs" role="tablist" aria-label="Workload types">
              {WORKLOAD_TYPES.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  role="tab"
                  aria-selected={workloadType === type.id}
                  className={`cw-tab${workloadType === type.id ? " is-active" : ""}`}
                  onClick={() => {
                    setWorkloadType(type.id);
                    if (type.id !== "pods") closeLiveStatus();
                  }}
                >
                  <i className={type.icon} aria-hidden="true" />
                  {type.label}
                </button>
              ))}
            </div>
            <div className="cw-filters">
              <div className={`cw-ns-select${statusMenuOpen ? " is-open" : ""}`} ref={statusSelectRef}>
                <button
                  type="button"
                  className={`cw-ns-select-btn${statusMenuOpen ? " is-open" : ""}${statusFilter === "all" ? " is-all" : ""}`}
                  aria-label="Status"
                  aria-expanded={statusMenuOpen}
                  onClick={() => {
                    setClusterMenuOpen(false);
                    setNsMenuOpen(false);
                    setNsQuery("");
                    setStatusMenuOpen((open) => {
                      const next = !open;
                      if (!next) setStatusQuery("");
                      return next;
                    });
                  }}
                >
                  <i className="fa-solid fa-heart-pulse cw-ns-select-ico" aria-hidden="true" />
                  <span className="cw-ns-select-value">{statusFilterLabel}</span>
                  <i className={`fa-solid fa-chevron-${statusMenuOpen ? "up" : "down"} cw-ns-select-ico`} aria-hidden="true" />
                </button>
                {statusMenuOpen ? (
                  <div className="cw-ns-menu" role="listbox" aria-label="Statuses">
                    <div className="cw-ns-search">
                      <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                      <input
                        type="search"
                        autoFocus
                        value={statusQuery}
                        placeholder="Search status…"
                        onChange={(e) => setStatusQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="cw-ns-list">
                      {searchableStatuses.length ? (
                        searchableStatuses.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            role="option"
                            aria-selected={statusFilter === opt.id}
                            className={`cw-ns-option${statusFilter === opt.id ? " is-active" : ""}${opt.id === "all" ? " is-all" : ""}`}
                            onClick={() => {
                              applyStatusFilter(opt.id);
                              setStatusMenuOpen(false);
                              setStatusQuery("");
                            }}
                          >
                            <span className="cw-ns-option-name">{opt.label}</span>
                            {statusFilter === opt.id ? <i className="fa-solid fa-check" style={{ fontSize: 11, color: "#3d91e4" }} /> : null}
                          </button>
                        ))
                      ) : (
                        <div className="cw-ns-empty">No status matched “{statusQuery}”</div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className={`cw-ns-select${nsMenuOpen ? " is-open" : ""}`} ref={nsSelectRef}>
                <button
                  type="button"
                  className={`cw-ns-select-btn${nsMenuOpen ? " is-open" : ""}${namespace === "All namespaces" ? " is-all" : ""}`}
                  aria-label="Namespace"
                  aria-expanded={nsMenuOpen}
                  onClick={() => {
                    setClusterMenuOpen(false);
                    setStatusMenuOpen(false);
                    setStatusQuery("");
                    setNsMenuOpen((open) => {
                      const next = !open;
                      if (!next) setNsQuery("");
                      return next;
                    });
                  }}
                >
                  <i className="fa-solid fa-layer-group cw-ns-select-ico" aria-hidden="true" />
                  <span className="cw-ns-select-value">{namespace}</span>
                  <i className={`fa-solid fa-chevron-${nsMenuOpen ? "up" : "down"} cw-ns-select-ico`} aria-hidden="true" />
                </button>
                {nsMenuOpen ? (
                  <div className="cw-ns-menu" role="listbox" aria-label="Namespaces">
                    <div className="cw-ns-search">
                      <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                      <input
                        type="search"
                        autoFocus
                        value={nsQuery}
                        placeholder="Search namespace…"
                        onChange={(e) => setNsQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="cw-ns-list">
                      {searchableNamespaces.length ? (
                        searchableNamespaces.map((ns) => (
                          <button
                            key={ns}
                            type="button"
                            role="option"
                            aria-selected={namespace === ns}
                            className={`cw-ns-option${namespace === ns ? " is-active" : ""}${ns === "All namespaces" ? " is-all" : ""}`}
                            onClick={() => {
                              applyNamespace(ns);
                              setNsMenuOpen(false);
                              setNsQuery("");
                            }}
                          >
                            <span className="cw-ns-option-name">{ns}</span>
                            {namespace === ns ? <i className="fa-solid fa-check" style={{ fontSize: 11, color: "#3d91e4" }} /> : null}
                          </button>
                        ))
                      ) : (
                        <div className="cw-ns-empty">No namespace matched “{nsQuery}”</div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="cw-split">
            <div className="cw-table-wrap">
              <table className="cw-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Namespace</th>
                    <th>Status</th>
                    <th>{isPodsTab ? "Zone" : "Age"}</th>
                    {isPodsTab ? <th className="cw-col-actions">Action</th> : null}
                  </tr>
                </thead>
                {tableBusy ? (
                  <TableSkeleton />
                ) : isPodsTab && filteredPods.length > 0 ? (
                  <tbody>
                    {filteredPods.map((pod) => {
                      const st = getPodStatusStyle(pod.status);
                      const selected = selectedPod && (selectedPod.id === pod.id || podKey(selectedPod) === podKey(pod));
                      return (
                        <tr
                          key={pod.id}
                          className={`cw-row-click${selected ? " is-selected" : ""}`}
                          onClick={() => openLiveStatus(pod)}
                        >
                          <td>
                            <div className="cw-row-name">{pod.projectName || pod.name || "—"}</div>
                          </td>
                          <td><CopyNamespace value={pod.namespace} /></td>
                          <td>
                            <SATooltip title="Live status">
                              <button
                                type="button"
                                className={`cw-live-btn${selected ? " is-listening" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openLiveStatus(pod);
                                }}
                                aria-label={`Live status for ${pod.name}`}
                              >
                                <span className="cw-status" style={{ background: st.bg, color: st.color }}>
                                  <span className="cw-status-dot" />
                                  {podStatusLabel(pod.status)}
                                </span>
                                <span className="cw-live-icon">
                                  <i className={`fa-solid fa-tower-broadcast${selected ? " is-pulse" : ""}`} />
                                </span>
                              </button>
                            </SATooltip>
                          </td>
                          <td className="cw-mono">{pod.zone || pod.age || "—"}</td>
                          <td className="cw-col-actions">
                            <div className="cw-row-actions">
                              {getPodStatusStyle(pod.status) === STATUS_STYLE.STOPPED ? (
                                <span className="cw-status" style={{ background: STATUS_STYLE.STOPPED.bg, color: STATUS_STYLE.STOPPED.color }}>
                                  Stopped
                                </span>
                              ) : (
                                  <button
                                    type="button"
                                    className="cw-stop-btn"
                                    disabled={deletingPodId === getLivePodId(pod) || !getLivePodId(pod) || pod.status === "PROCESSING"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      askDeletePod(pod);
                                    }}
                                  >
                                    <i className={`fa-solid ${deletingPodId === getLivePodId(pod) ? "fa-spinner fa-spin" : "fa-circle-stop"}`} />
                                    {deletingPodId === getLivePodId(pod) ? "Stopping" : "Stop"}
                                  </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={5}>
                        <div className="cw-empty">
                          <div className="cw-empty-art">
                            <i className={activeType.icon} />
                          </div>
                          <h3>
                            {clusters.length === 0
                              ? "Create a cluster to get started"
                              : `No ${activeType.label.toLowerCase()} found`}
                          </h3>
                          <p>
                            {clusters.length === 0
                              ? "Provision a Kubernetes cluster first, then return here to inspect live workloads."
                              : selectedCluster
                                ? isPodsTab
                                  ? `No pods matched in ${selectedCluster.name || selectedCluster.clusterName} for ${namespace.toLowerCase()}${statusFilter !== "all" ? ` · ${POD_STATUS_FILTERS.find((o) => o.id === statusFilter)?.label || statusFilter}` : ""}.`
                                  : `Nothing matched in ${selectedCluster.name || selectedCluster.clusterName} for ${namespace.toLowerCase()}. Workload sync will populate this table when connected.`
                                : "Select a cluster above to browse resources."}
                          </p>
                          <div className="cw-empty-steps">
                            <span className="cw-empty-step">1 · Pick cluster</span>
                            <span className="cw-empty-step">2 · Choose type</span>
                            <span className="cw-empty-step">3 · Filter namespace</span>
                          </div>
                          {clusters.length === 0 ? (
                            <div style={{ marginTop: 16 }}>
                              <button type="button" className="cw-btn primary" onClick={goToClusters}>
                                Go to Clusters
                              </button>
                            </div>
                          ) : null}
                          {selectedCluster ? (
                            <div style={{ marginTop: 14 }}>
                              <span className="cw-status" style={{ background: clusterStatus.bg, color: clusterStatus.color }}>
                                <span className="cw-status-dot" />
                                Cluster {clusterStatus.label}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
            {isPodsTab && !tableBusy && totalPages > 1 ? (
              <div className="cw-pagination">
                <SAPagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalElements || filteredPods.length}
                  itemsPerPage={pageSize || WORKLOAD_PAGE_SIZE}
                  onPageChange={setPage}
                  showGoToPage
                  size="sm"
                  className="cw-pagination-bar"
                />
              </div>
            ) : null}
          </div>
        </div>

            {showLive ? (
              <aside className="cw-live-panel" aria-label={`Live status for ${selectedPod.name}`}>
                <div className="cw-live-head">
                  <div>
                    <div className="cw-live-kicker">
                      <i className={`fa-solid fa-tower-broadcast${liveLoading ? " is-pulse" : ""}`} />
                      Live status
                    </div>
                    <div className="cw-live-title">{selectedPod.name}</div>
                    <div className="cw-live-sub">
                      <CopyNamespace value={selectedPod.namespace} />
                      {selectedPod.clusterName ? (
                        <span className="cw-live-sub-extra"> · {selectedPod.clusterName}</span>
                      ) : null}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {selectedStatus !== STATUS_STYLE.STOPPED ? (
                      <button
                        type="button"
                        className="cw-stop-btn"
                        disabled={deletingPodId === getLivePodId(selectedPod) || !getLivePodId(selectedPod)}
                        onClick={() => askDeletePod(selectedPod)}
                      >
                        <i className={`fa-solid ${deletingPodId === getLivePodId(selectedPod) ? "fa-spinner fa-spin" : "fa-circle-stop"}`} />
                        {deletingPodId === getLivePodId(selectedPod) ? "Stopping" : "Stop"}
                      </button>
                    ) : null}
                    <button type="button" className="cw-live-close" onClick={closeLiveStatus} aria-label="Close live status">
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                </div>
                {liveError ? <div className="cw-live-error">{liveError}</div> : null}
                <div className="cw-live-meta">
                  <div className="cw-live-meta-item">
                    <div className="cw-live-meta-label">Status</div>
                    <div className="cw-live-meta-value">
                      <span className="cw-status" style={{ background: selectedStatus.bg, color: selectedStatus.color }}>
                        <span className="cw-status-dot" />
                        {podStatusLabel(selectedPod.status)}
                      </span>
                    </div>
                  </div>
                  <div className="cw-live-meta-item">
                    <div className="cw-live-meta-label">Namespace</div>
                    <div className="cw-live-meta-value">
                      <CopyNamespace value={selectedPod.namespace} />
                    </div>
                  </div>
                  <div className="cw-live-meta-item">
                    <div className="cw-live-meta-label">Ready</div>
                    <div className="cw-live-meta-value">{selectedPod.ready}</div>
                  </div>
                  <div className="cw-live-meta-item">
                    <div className="cw-live-meta-label">Zone</div>
                    <div className="cw-live-meta-value">{selectedPod.zone || "—"}</div>
                  </div>
                  <div className="cw-live-meta-item">
                    <div className="cw-live-meta-label">IP</div>
                    <div className="cw-live-meta-value">{selectedPod.ip || "—"}</div>
                  </div>
                  {selectedPod.message ? (
                    <div className="cw-live-meta-item" style={{ gridColumn: "1 / -1" }}>
                      <div className="cw-live-meta-label">Message</div>
                      <div className="cw-live-meta-value">{selectedPod.message}</div>
                    </div>
                  ) : null}
                </div>
              </aside>
            ) : null}
      </div>
      {podToDelete ? (
        <div className="cw-confirm-backdrop" role="presentation" onClick={cancelDeletePod}>
          <div
            className="cw-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cw-stop-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cw-confirm-icon">
              <i className="fa-solid fa-circle-stop" />
            </div>
            <h3 id="cw-stop-title">Stop this workload?</h3>
            <p>This removes the workload from the cluster. It will show as Stopped after the request completes.</p>
            <div className="cw-confirm-pod">
              <div>
                <div className="cw-confirm-pod-name">{podToDelete.projectName}</div>
                <div className="cw-confirm-pod-ns">{podToDelete.namespace}</div>
              </div>
              <span className="cw-status" style={{ background: getPodStatusStyle(podToDelete.status).bg, color: getPodStatusStyle(podToDelete.status).color }}>
                <span className="cw-status-dot" />
                {podStatusLabel(podToDelete.status)}
              </span>
            </div>
            <div className="cw-confirm-actions">
              <button type="button" className="cw-btn" onClick={cancelDeletePod} disabled={!!deletingPodId}>
                Cancel
              </button>
              <button type="button" className="cw-btn danger" onClick={confirmDeletePod} disabled={!!deletingPodId}>
                {deletingPodId ? "Stopping…" : "Stop workload"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ClusterWorkloads;
